import React, { useState, useEffect, useCallback } from 'react';
import { Bell, UserPlus, Check, X, CheckCircle, XCircle, Calendar, AlertTriangle, CreditCard, Home, BarChart3, RefreshCw, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api, { householdAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import '../styles/Notifications.css';

/**
 * Notifications - Dropdown notification panel for navbar
 * Shows quick preview of recent notifications
 */
function Notifications({ user, onCountChange, onClose }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const { success, error: showError } = useToast();

  const isManager = user?.household_role === 'manager';
  const hasHousehold = !!user?.household_id;

  // Refresh count function
  const refreshCount = useCallback(async () => {
    try {
      let totalCount = 0;
      
      const notifResponse = await api.get('/notifications/unread-count');
      totalCount += notifResponse.data.count;
      
      if (isManager && hasHousehold) {
        try {
          const requestsResponse = await householdAPI.getJoinRequestsCount();
          totalCount += requestsResponse.data.count;
        } catch (e) {
          // Ignore
        }
      }
      
      setCount(totalCount);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  }, [isManager, hasHousehold]);

  // Update parent when count changes
  useEffect(() => {
    if (onCountChange) {
      onCountChange(count);
    }
  }, [count, onCountChange]);

  // Load count periodically
  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 30000);
    return () => clearInterval(interval);
  }, [refreshCount]);

  // Load data immediately
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const notifResponse = await api.get('/notifications');
        // Only show most recent 8 in dropdown
        setNotifications(notifResponse.data.slice(0, 8));
        
        if (isManager && hasHousehold) {
          try {
            const requestsResponse = await householdAPI.getJoinRequests();
            setJoinRequests(requestsResponse.data.slice(0, 3));
          } catch (e) {
            setJoinRequests([]);
          }
        } else {
          setJoinRequests([]);
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
        showError('Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isManager, hasHousehold, showError]);

  const handleApprove = async (requestId, name) => {
    setProcessing(requestId);
    try {
      await householdAPI.processJoinRequest(requestId, 'approve');
      success(`${name} has been added to your household`);
      setJoinRequests(joinRequests.filter(r => r.id !== requestId));
      refreshCount();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(requestId);
    try {
      await householdAPI.processJoinRequest(requestId, 'reject');
      success('Join request rejected');
      setJoinRequests(joinRequests.filter(r => r.id !== requestId));
      refreshCount();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    
    if (notification.data?.url) {
      if (onClose) onClose();
      navigate(notification.data.url);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      refreshCount();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      refreshCount();
      success('All notifications marked as read');
    } catch (error) {
      showError('Failed to mark all as read');
    }
  };

  const renderNotificationIcon = (type) => {
    const iconProps = { size: 18 };
    const iconMap = {
      'join_request_approved': <CheckCircle {...iconProps} className="icon-success" />,
      'invitation_accepted': <CheckCircle {...iconProps} className="icon-success" />,
      'join_request_rejected': <XCircle {...iconProps} className="icon-danger" />,
      'invitation_rejected': <XCircle {...iconProps} className="icon-danger" />,
      'join_request_pending': <Home {...iconProps} className="icon-primary" />,
      'household_invitation': <Home {...iconProps} className="icon-primary" />,
      'bill_due_soon': <Calendar {...iconProps} className="icon-warning" />,
      'bill_overdue': <AlertTriangle {...iconProps} className="icon-danger" />,
      'payment_received': <CreditCard {...iconProps} className="icon-success" />,
      'autopay_processed': <RefreshCw {...iconProps} className="icon-primary" />,
      'weekly_summary': <BarChart3 {...iconProps} className="icon-primary" />,
      'security_new_login': <Shield {...iconProps} className="icon-warning" />,
      'security_password_changed': <Shield {...iconProps} className="icon-warning" />,
      'security_mfa_enabled': <Shield {...iconProps} className="icon-success" />,
      'security_mfa_disabled': <Shield {...iconProps} className="icon-danger" />,
      'test': <Bell {...iconProps} className="icon-primary" />
    };
    
    return iconMap[type] || <Bell {...iconProps} />;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const totalItems = notifications.length + joinRequests.length;
  const hasUnreadNotifications = notifications.some(n => !n.is_read);

  return (
    <div className="notifications-dropdown">
      <div className="notifications-header">
        <div className="notifications-header-left">
          <h3>Notifications</h3>
          {count > 0 && <span className="count-badge">{count}</span>}
        </div>
        {totalItems > 0 && hasUnreadNotifications && (
          <button 
            className="header-action-btn"
            onClick={handleMarkAllAsRead}
            title="Mark all as read"
          >
            <Check size={16} />
          </button>
        )}
      </div>

      <div className="notifications-body">
        {loading ? (
          <div className="notifications-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : totalItems === 0 ? (
          <div className="notifications-empty">
            <Bell size={40} />
            <p>No notifications</p>
          </div>
        ) : (
          <div className="notifications-list">
            {/* Join Requests */}
            {joinRequests.map(request => (
              <div 
                key={`request-${request.id}`} 
                className="notification-item unread"
              >
                <div className="notification-icon">
                  <UserPlus size={18} className="icon-primary" />
                </div>
                <div className="notification-content">
                  <div className="notification-title">
                    <strong>Join Request</strong>
                  </div>
                  <div className="notification-message">
                    {request.first_name} {request.last_name}
                  </div>
                  <div className="notification-time">
                    {formatTimeAgo(request.created_at)}
                  </div>
                </div>
                <div className="notification-actions">
                  <button
                    className="btn-approve"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApprove(request.id, `${request.first_name} ${request.last_name}`);
                    }}
                    disabled={processing === request.id}
                    title="Approve"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    className="btn-reject"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReject(request.id);
                    }}
                    disabled={processing === request.id}
                    title="Reject"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
            
            {/* Notifications */}
            {notifications.map(notification => (
              <div 
                key={`notif-${notification.id}`} 
                className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-icon">
                  {renderNotificationIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className="notification-title">
                    <strong>{notification.title}</strong>
                  </div>
                  <div className="notification-message">{notification.message}</div>
                  <div className="notification-time">
                    {formatTimeAgo(notification.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {totalItems > 0 && (
        <div className="notifications-footer">
          <span className="notifications-hint">Click notifications to view details</span>
        </div>
      )}
    </div>
  );
}

export default Notifications;
